import { isTitlesTooBroad } from './titlesErrors';

type SearchTerm = { clientName?: string; name?: string };

/**
 * `placeholderData` for the titles list. Holds the previous result only while
 * the search term is unchanged (page, sort or filter changes), so the rows stay
 * on screen while the next page loads. A different `name` or `clientName`
 * returns `undefined`: another term's rows, or the unfiltered list, must never
 * stand in for it.
 */
export const keepPreviousForSameSearch = <T>(
  next: SearchTerm,
  previousParams: SearchTerm | undefined,
  previousData: T | undefined,
) =>
  previousParams &&
  (previousParams.name ?? '') === (next.name ?? '') &&
  (previousParams.clientName ?? '') === (next.clientName ?? '')
    ? previousData
    : undefined;

const normalised = (term?: string) => (term ?? '').trim().toLowerCase();

/**
 * True when `next` is the too-broad `previous` term with more typed onto it
 * (or the same term). Its matches are a subset of the refused search's, so the
 * alert still describes it until the response says otherwise.
 */
export const narrowsClientSearch = (
  next: { clientName?: string },
  previous?: { clientName?: string },
) => {
  const previousTerm = normalised(previous?.clientName);

  return (
    previousTerm !== '' && normalised(next.clientName).includes(previousTerm)
  );
};

/**
 * `placeholderData` for the Titles page's search query: the rule above, plus
 * one case. A too-broad alert stays up while a term that narrows it loads, so
 * the alert does not flicker out for a skeleton and back. Only that alert
 * crosses terms; rows never do.
 */
export const keepPreviousSearchResult = <T>(
  next: SearchTerm,
  previousParams: SearchTerm | undefined,
  previousData: T | undefined,
) =>
  keepPreviousForSameSearch(next, previousParams, previousData) ??
  (isTitlesTooBroad(previousData) && narrowsClientSearch(next, previousParams)
    ? previousData
    : undefined);
